import { normalizePhysicalSamplingConfig } from '@/components/web/components/PhysicalProductSelection/adapters';
import { SamplingMode } from '@/components/web/components/PhysicalProductSelection/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'aha-components';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import {
  ProductType,
  ProductPhysicalSelectEnum
} from '@/components/web/config';
import {
  SquareWarnIcon,
  CheckLinearIcon,
  UpgradeWarnIcon
} from '@/components/common/icon';
import { isMobile } from '@/biz/tool';
import {
  NationsLab,
  buildCountrySelectOptions,
  getCountryName
} from '@/utils/locations';
import { hasSubmittedScheduleConflict } from '@/utils/creatorScheduleConflict';
import CreatorScheduleForm from './CreatorScheduleForm';
import ScreeningQuestions from './ScreeningQuestions';

export function CreatorProductGiftNotice() {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const sampling = normalizePhysicalSamplingConfig(detailData?.product);
  const multiple = sampling?.mode === SamplingMode.MULTIPLE_PRODUCTS;
  return (
    <>
      {detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
        detailData?.product?.physicalProductShipType ===
          ProductPhysicalSelectEnum.ship && (
          <div className="px-4 py-2 bg-[#EFF6FF] rounded-lg mb-3 flex items-start gap-2">
            <div className="w-6 h-6">
              <SquareWarnIcon className="w-6 h-6" />
            </div>
            <div
              className={`flex ${isMobile() ? 'flex-col justify-start items-start' : 'items-center'}`}
            >
              <span>
                {multiple ? (
                  <span>
                    {t('physical_product.sampling.multiple_product_notice', {
                      defaultValue:
                        'This collaboration includes physical products. After it is confirmed, choose up to {countNum} from {total} available products.',
                      interpolation: { prefix: '{', suffix: '}' },
                      countNum: sampling.maxSelectableProducts,
                      total: sampling.products.length
                    })}
                  </span>
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('price.fields.receiving_product', {
                        defaultValue:
                          'You’ll receive a free product (worth <b>$' +
                          '{price}</b>) as part of this collaboration. Many creators consider this when setting their price',
                        price: detailData?.product?.physicalProductCashValue
                      })
                    }}
                  ></span>
                )}
                {!multiple ? <span>{'.'}</span> : null}
              </span>
            </div>
          </div>
        )}
    </>
  );
}

/** Unchanged initial-quote requirements shared by legacy, PC and H5 package flows. */
export default function CreatorQuoteRequirements({
  selectCountryError = false,
  setSelectCountryError,
  scheduleError = false,
  screeningSubmissionError
}: {
  selectCountryError?: boolean;
  setSelectCountryError?: (value: boolean) => void;
  scheduleError?: boolean;
  screeningSubmissionError?: string | null;
}) {
  const { t } = useTranslation();
  const [countryOpen, setCountryOpen] = useState(false);
  const { detailData } = useDetailDataStore();
  const { product, order, campaign } = detailData || {};
  const {
    setAgreeProductShip,
    productShippingCountry,
    setProductShippingCountry
  } = useWebModalStore();
  const locations = campaign?.locations || [];
  const countryCodes = locations.map((item: string[]) => item?.[1]);
  const campaignCountries = countryCodes;
  const countries = countryCodes
    .map((code: string) => NationsLab?.[code])
    .join(', ');
  const isProductShip =
    product?.productType === ProductType.PHYSICAL_PRODUCT &&
    product?.physicalProductShipType === ProductPhysicalSelectEnum.ship;
  const scheduleConflictSubmitted = hasSubmittedScheduleConflict(
    order?.scheduleConflict
  );
  return (
    <div className="flex w-full flex-col gap-4" data-creator-quote-requirements>
      <ScreeningQuestions />
      {screeningSubmissionError ? (
        <div
          role="alert"
          data-screening-submission-error
          className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#DC2626]"
        >
          {screeningSubmissionError}
        </div>
      ) : null}
      {!scheduleConflictSubmitted && (
        <CreatorScheduleForm
          id="take-order-schedule-conflict"
          validationError={scheduleError}
        />
      )}
      {isProductShip && (
        <div
          id="take-order-shipping-country"
          className="mt-4 mb-2"
          onKeyDownCapture={(event) => {
            if (countryOpen && event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setCountryOpen(false);
            }
          }}
        >
          <div className="text-[#0D0D0D] text-base font-semibold leading-6 mb-2">
            {t('interest.shipping_country.title', {
              defaultValue: 'Product shipping country'
            })}
            <span className="text-red-500"> *</span>
          </div>
          <Select
            open={countryOpen}
            onDropdownVisibleChange={setCountryOpen}
            error={selectCountryError}
            showSearch
            value={productShippingCountry || undefined}
            onChange={(value) => {
              const selected = (value as string) || '';
              if (selectCountryError) {
                setSelectCountryError?.(!campaignCountries?.includes(selected));
              }
              setProductShippingCountry(selected);
              setAgreeProductShip(campaignCountries?.includes(value)); // 判断地区是否符合
            }}
            options={buildCountrySelectOptions()}
            placeholder={t('interest.shipping_country.please_select', {
              defaultValue: 'Please select'
            })}
            // 因为移动端的层级是9999，所以需要设置一个更高的层级
            dropdownStyle={{ zIndex: 10000 }}
          />
          {selectCountryError ? (
            <div className="text-red-500 text-sm mt-2">
              {productShippingCountry
                ? t('campaign.modal.submit_interest.location_notice', {
                    defaultValue: 'Please confirm locations.'
                  })
                : t('interest.shipping_country.please_select_country', {
                    defaultValue: 'This is a required field.'
                  })}
            </div>
          ) : null}
          {productShippingCountry ? (
            campaignCountries?.includes(productShippingCountry) ? (
              <div className="flex items-center gap-2 mt-2 text-sm font-medium text-[#079455]">
                <CheckLinearIcon className="w-6 h-6 text-[#079455]" />
                <span>
                  {t('interest.shipping_country.description', {
                    country: getCountryName(productShippingCountry)
                  })}
                </span>
              </div>
            ) : (
              <div className="bg-[#FEF2F2] leading-5 px-4 py-3 rounded-lg mt-2">
                <div className="flex items-start gap-2">
                  <div className="mt-[2px]">
                    <UpgradeWarnIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-[#292524] text-base font-semibold">
                      {t('interest.shipping_country.cannot_ship_title', {
                        country: getCountryName(productShippingCountry)
                      })}
                    </div>
                    <div className="text-[#292524] text-sm font-normal mt-2">
                      {t('interest.shipping_country.cannot_ship_description', {
                        countries: countries
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : null}
        </div>
      )}
    </div>
  );
}
